import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  makeStyles,
  TextField,
  Grid,
  Button,
  Typography,
} from "@material-ui/core";
import { Formik, Field, FastField, FieldArray } from "formik";
import { isArray } from "lodash";
import NumberFormat from "react-number-format";
import { Copy, Eraser, Briefcase, CalendarOff, MessageSquareText, Workflow, HelpCircle } from "lucide-react";

import ButtonWithSpinner from "../ButtonWithSpinner";
import CustomToggleSwitch from "../CustomToggleSwitch";
import AppTooltip from "../AppTooltip";
import { i18n } from "../../translate/i18n";

const WEEKEND_DAYS = ["saturday", "sunday"];
const WEEKDAY_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const ORDERED_DAYS = [...WEEKDAY_DAYS, ...WEEKEND_DAYS];
const DAY_ALIASES = {
  monday: "monday",
  segunda: "monday",
  segundafeira: "monday",
  tuesday: "tuesday",
  terca: "tuesday",
  tercafeira: "tuesday",
  wednesday: "wednesday",
  quarta: "wednesday",
  quartafeira: "wednesday",
  thursday: "thursday",
  quinta: "thursday",
  quintafeira: "thursday",
  friday: "friday",
  sexta: "friday",
  sextafeira: "friday",
  saturday: "saturday",
  sabado: "saturday",
  sunday: "sunday",
  domingo: "sunday",
};

const DEFAULT_BUSINESS_HOURS = {
  startTimeA: "09:00",
  endTimeA: "12:00",
  startTimeB: "13:00",
  endTimeB: "18:00",
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    root: { width: "100%" },
    fullWidth: { width: "100%" },

    section: {
      marginBottom: theme.spacing(2),
    },
    messageCard: {
      borderRadius: 12,
      border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      padding: 16,
      marginBottom: 16,
    },
    messageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 12,
    },
    messageTitle: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: 14,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#1a1a1a",
    },
    helpIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      color: isDark ? "#8696a0" : "#667781",
      cursor: "help",
      opacity: 0.6,
      transition: "opacity 0.15s ease, color 0.15s ease",
      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
      },
    },
    helpTooltipTitle: {
      fontWeight: 600,
      marginBottom: 6,
      fontSize: 12,
    },
    helpTooltipSteps: {
      margin: 0,
      paddingLeft: 16,
      "& li": { marginBottom: 3 },
      "& li:last-child": { marginBottom: 0 },
    },
    messageDesc: {
      fontSize: 12,
      color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
      marginTop: 4,
      marginBottom: 8,
    },
    messageField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
    },

    bulkBar: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginBottom: 16,
    },
    bulkBtn: {
      textTransform: "none",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 500,
      padding: "6px 12px",
      gap: 6,
      borderColor: isDark ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.20)",
      color: isDark ? "#e9edef" : "#1a1a1a",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
    },

    dayCard: {
      borderRadius: 12,
      border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      padding: 12,
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)",
      },
    },
    dayHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 10,
    },
    dayName: {
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#1a1a1a",
    },
    dayClosedHint: {
      fontSize: 12,
      color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
      fontStyle: "italic",
      marginTop: 8,
      textAlign: "center",
      padding: "12px 0",
    },
    shiftLabel: {
      fontSize: 11,
      fontWeight: 500,
      color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
      marginBottom: 4,
      marginTop: 4,
    },
    timeField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
      },
    },

    buttonContainer: {
      textAlign: "right",
      paddingTop: theme.spacing(2),
    },
  };
});

const buildDefaultSchedules = () => [
  { weekday: i18n.t("queueModal.serviceHours.monday"), weekdayEn: "monday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "" },
  { weekday: i18n.t("queueModal.serviceHours.tuesday"), weekdayEn: "tuesday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "" },
  { weekday: i18n.t("queueModal.serviceHours.wednesday"), weekdayEn: "wednesday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "" },
  { weekday: i18n.t("queueModal.serviceHours.thursday"), weekdayEn: "thursday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "" },
  { weekday: i18n.t("queueModal.serviceHours.friday"), weekdayEn: "friday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "" },
  { weekday: i18n.t("queueModal.serviceHours.saturday"), weekdayEn: "saturday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "" },
  { weekday: i18n.t("queueModal.serviceHours.sunday"), weekdayEn: "sunday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "" },
];

const normalizeDayKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z]/g, "");

const getScheduleDayKey = (day, index) => {
  const weekdayEnKey = DAY_ALIASES[normalizeDayKey(day?.weekdayEn)];
  if (weekdayEnKey) return weekdayEnKey;

  const weekdayKey = DAY_ALIASES[normalizeDayKey(day?.weekday)];
  if (weekdayKey) return weekdayKey;

  return ORDERED_DAYS[index] || "";
};

const normalizeSchedules = (items) => {
  const defaults = buildDefaultSchedules();

  return items.map((item, index) => ({
    ...defaults[index],
    ...item,
    weekday: item?.weekday || defaults[index]?.weekday || "",
    weekdayEn: getScheduleDayKey(item, index) || defaults[index]?.weekdayEn || "",
  }));
};

const isDayClosed = (day) =>
  !day.startTimeA && !day.endTimeA && !day.startTimeB && !day.endTimeB;

function SchedulesForm(props) {
  const {
    initialValues,
    onSubmit,
    loading,
    labelSaveButton,
    showOutOfHoursMessage = false,
    initialOutOfHoursMessage = "",
    initialKeepFlowsOutOfHours = false,
  } = props;

  const classes = useStyles();
  const [schedules, setSchedules] = useState(buildDefaultSchedules);
  const [outOfHoursEnabled, setOutOfHoursEnabled] = useState(
    !!initialOutOfHoursMessage
  );
  const [outOfHoursMessage, setOutOfHoursMessage] = useState(
    initialOutOfHoursMessage || ""
  );
  const [keepFlowsOutOfHours, setKeepFlowsOutOfHours] = useState(
    !!initialKeepFlowsOutOfHours
  );

  useEffect(() => {
    if (isArray(initialValues) && initialValues.length > 0) {
      setSchedules(normalizeSchedules(initialValues));
    }
  }, [initialValues]);

  useEffect(() => {
    setOutOfHoursMessage(initialOutOfHoursMessage || "");
    setOutOfHoursEnabled(!!initialOutOfHoursMessage);
  }, [initialOutOfHoursMessage]);

  useEffect(() => {
    setKeepFlowsOutOfHours(!!initialKeepFlowsOutOfHours);
  }, [initialKeepFlowsOutOfHours]);

  const handleSubmit = (data) => {
    if (showOutOfHoursMessage) {
      onSubmit(data, {
        outOfHoursMessage: outOfHoursEnabled ? outOfHoursMessage : "",
        keepFlowsOutOfHours,
      });
    } else {
      onSubmit(data);
    }
  };

  // Constrói o conteúdo do tooltip de ajuda (description + steps) a partir do i18n
  const buildHelpContent = (helpKey) => {
    const data = i18n.t(`schedulesForm.helps.${helpKey}`, {
      returnObjects: true,
      defaultValue: null,
    });
    if (!data || typeof data === "string") return null;
    const description = data.description || "";
    const steps = Array.isArray(data.steps) ? data.steps : [];
    if (!description && steps.length === 0) return null;
    return (
      <div>
        {description && <div className={classes.helpTooltipTitle}>{description}</div>}
        {steps.length > 0 && (
          <ol className={classes.helpTooltipSteps}>
            {steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  const renderHelpIcon = (helpKey) => {
    const content = buildHelpContent(helpKey);
    if (!content) return null;
    return (
      <AppTooltip title={content} enterTouchDelay={0} leaveTouchDelay={6000}>
        <span className={classes.helpIcon}>
          <HelpCircle size={14} strokeWidth={2} />
        </span>
      </AppTooltip>
    );
  };

  return (
    <Formik
      enableReinitialize
      className={classes.fullWidth}
      initialValues={{ schedules }}
      onSubmit={({ schedules: scheds }) =>
        setTimeout(() => {
          handleSubmit(scheds);
        }, 500)
      }
    >
      {({ values, setFieldValue, handleSubmit }) => {
        const fillWeekdaysBusinessHours = () => {
          values.schedules.forEach((day, idx) => {
            if (WEEKDAY_DAYS.includes(getScheduleDayKey(day, idx))) {
              setFieldValue(`schedules[${idx}].startTimeA`, DEFAULT_BUSINESS_HOURS.startTimeA);
              setFieldValue(`schedules[${idx}].endTimeA`, DEFAULT_BUSINESS_HOURS.endTimeA);
              setFieldValue(`schedules[${idx}].startTimeB`, DEFAULT_BUSINESS_HOURS.startTimeB);
              setFieldValue(`schedules[${idx}].endTimeB`, DEFAULT_BUSINESS_HOURS.endTimeB);
            }
          });
        };

        const closeWeekend = () => {
          values.schedules.forEach((day, idx) => {
            if (WEEKEND_DAYS.includes(getScheduleDayKey(day, idx))) {
              setFieldValue(`schedules[${idx}].startTimeA`, "");
              setFieldValue(`schedules[${idx}].endTimeA`, "");
              setFieldValue(`schedules[${idx}].startTimeB`, "");
              setFieldValue(`schedules[${idx}].endTimeB`, "");
            }
          });
        };

        const copyFromFirst = () => {
          const first = values.schedules[0];
          if (!first) return;
          values.schedules.forEach((_, idx) => {
            if (idx === 0) return;
            setFieldValue(`schedules[${idx}].startTimeA`, first.startTimeA || "");
            setFieldValue(`schedules[${idx}].endTimeA`, first.endTimeA || "");
            setFieldValue(`schedules[${idx}].startTimeB`, first.startTimeB || "");
            setFieldValue(`schedules[${idx}].endTimeB`, first.endTimeB || "");
          });
        };

        const clearAll = () => {
          values.schedules.forEach((_, idx) => {
            setFieldValue(`schedules[${idx}].startTimeA`, "");
            setFieldValue(`schedules[${idx}].endTimeA`, "");
            setFieldValue(`schedules[${idx}].startTimeB`, "");
            setFieldValue(`schedules[${idx}].endTimeB`, "");
          });
        };

        const toggleDayOpen = (idx, willOpen) => {
          if (willOpen) {
            setFieldValue(`schedules[${idx}].startTimeA`, DEFAULT_BUSINESS_HOURS.startTimeA);
            setFieldValue(`schedules[${idx}].endTimeA`, DEFAULT_BUSINESS_HOURS.endTimeA);
            setFieldValue(`schedules[${idx}].startTimeB`, DEFAULT_BUSINESS_HOURS.startTimeB);
            setFieldValue(`schedules[${idx}].endTimeB`, DEFAULT_BUSINESS_HOURS.endTimeB);
          } else {
            setFieldValue(`schedules[${idx}].startTimeA`, "");
            setFieldValue(`schedules[${idx}].endTimeA`, "");
            setFieldValue(`schedules[${idx}].startTimeB`, "");
            setFieldValue(`schedules[${idx}].endTimeB`, "");
          }
        };

        return (
          <div className={classes.fullWidth}>
            {showOutOfHoursMessage && (
              <>
                <div className={classes.messageCard}>
                  <div className={classes.messageHeader}>
                    <span className={classes.messageTitle}>
                      <Workflow size={16} strokeWidth={1.8} />
                      {i18n.t("schedulesForm.keepFlowsOutOfHours.title")}
                      {renderHelpIcon("keepFlowsOutOfHours")}
                    </span>
                    <CustomToggleSwitch
                      checked={keepFlowsOutOfHours}
                      onChange={(e) => setKeepFlowsOutOfHours(e.target.checked)}
                    />
                  </div>
                  <div className={classes.messageDesc}>
                    {i18n.t("schedulesForm.keepFlowsOutOfHours.description")}
                  </div>
                </div>

                <div className={classes.messageCard}>
                  <div className={classes.messageHeader}>
                    <span className={classes.messageTitle}>
                      <MessageSquareText size={16} strokeWidth={1.8} />
                      {i18n.t("schedulesForm.outOfHoursMessage.title")}
                      {renderHelpIcon("outOfHoursMessage")}
                    </span>
                    <CustomToggleSwitch
                      checked={outOfHoursEnabled}
                      onChange={(e) => setOutOfHoursEnabled(e.target.checked)}
                    />
                  </div>
                  <div className={classes.messageDesc}>
                    {i18n.t("schedulesForm.outOfHoursMessage.description")}
                  </div>
                  {outOfHoursEnabled && (
                    <TextField
                      className={classes.messageField}
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={6}
                      placeholder={i18n.t("schedulesForm.outOfHoursMessage.placeholder")}
                      value={outOfHoursMessage}
                      onChange={(e) => setOutOfHoursMessage(e.target.value)}
                    />
                  )}
                </div>
              </>
            )}

            <div className={classes.bulkBar}>
              <AppTooltip titleKey="schedulesForm.bulk.fillWeekdaysHelp">
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  className={classes.bulkBtn}
                  onClick={fillWeekdaysBusinessHours}
                  startIcon={<Briefcase size={14} strokeWidth={1.8} />}
                >
                  {i18n.t("schedulesForm.bulk.fillWeekdays")}
                </Button>
              </AppTooltip>
              <AppTooltip titleKey="schedulesForm.bulk.closeWeekendHelp">
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  className={classes.bulkBtn}
                  onClick={closeWeekend}
                  startIcon={<CalendarOff size={14} strokeWidth={1.8} />}
                >
                  {i18n.t("schedulesForm.bulk.closeWeekend")}
                </Button>
              </AppTooltip>
              <AppTooltip titleKey="schedulesForm.bulk.copyFirstHelp">
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  className={classes.bulkBtn}
                  onClick={copyFromFirst}
                  startIcon={<Copy size={14} strokeWidth={1.8} />}
                >
                  {i18n.t("schedulesForm.bulk.copyFirst")}
                </Button>
              </AppTooltip>
              <AppTooltip titleKey="schedulesForm.bulk.clearAllHelp">
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  className={classes.bulkBtn}
                  onClick={clearAll}
                  startIcon={<Eraser size={14} strokeWidth={1.8} />}
                >
                  {i18n.t("schedulesForm.bulk.clearAll")}
                </Button>
              </AppTooltip>
            </div>

            <FieldArray
              name="schedules"
              render={() => (
                <Grid spacing={2} container>
                  {values.schedules.map((item, index) => {
                    const closed = isDayClosed(item);
                    return (
                      <Grid key={index} xs={12} sm={6} md={4} item>
                        <div className={classes.dayCard}>
                          <div className={classes.dayHeader}>
                            <span className={classes.dayName}>{item.weekday}</span>
                            <CustomToggleSwitch
                              checked={!closed}
                              onChange={(e) => toggleDayOpen(index, e.target.checked)}
                            />
                          </div>
                          {closed ? (
                            <div className={classes.dayClosedHint}>
                              {i18n.t("schedulesForm.dayClosed")}
                            </div>
                          ) : (
                            <Grid container spacing={1}>
                              <Grid item xs={12}>
                                <div className={classes.shiftLabel}>
                                  {i18n.t("schedulesForm.morning")}
                                </div>
                              </Grid>
                              <Grid item xs={6}>
                                <Field name={`schedules[${index}].startTimeA`}>
                                  {({ field }) => (
                                    <NumberFormat
                                      {...field}
                                      variant="outlined"
                                      margin="dense"
                                      size="small"
                                      customInput={TextField}
                                      format="##:##"
                                      className={classes.timeField}
                                      placeholder="09:00"
                                      label={i18n.t("schedulesForm.start")}
                                    />
                                  )}
                                </Field>
                              </Grid>
                              <Grid item xs={6}>
                                <Field name={`schedules[${index}].endTimeA`}>
                                  {({ field }) => (
                                    <NumberFormat
                                      {...field}
                                      variant="outlined"
                                      margin="dense"
                                      size="small"
                                      customInput={TextField}
                                      format="##:##"
                                      className={classes.timeField}
                                      placeholder="12:00"
                                      label={i18n.t("schedulesForm.end")}
                                    />
                                  )}
                                </Field>
                              </Grid>
                              <Grid item xs={12}>
                                <div className={classes.shiftLabel}>
                                  {i18n.t("schedulesForm.afternoon")}
                                </div>
                              </Grid>
                              <Grid item xs={6}>
                                <Field name={`schedules[${index}].startTimeB`}>
                                  {({ field }) => (
                                    <NumberFormat
                                      {...field}
                                      variant="outlined"
                                      margin="dense"
                                      size="small"
                                      customInput={TextField}
                                      format="##:##"
                                      className={classes.timeField}
                                      placeholder="13:00"
                                      label={i18n.t("schedulesForm.start")}
                                    />
                                  )}
                                </Field>
                              </Grid>
                              <Grid item xs={6}>
                                <Field name={`schedules[${index}].endTimeB`}>
                                  {({ field }) => (
                                    <NumberFormat
                                      {...field}
                                      variant="outlined"
                                      margin="dense"
                                      size="small"
                                      customInput={TextField}
                                      format="##:##"
                                      className={classes.timeField}
                                      placeholder="18:00"
                                      label={i18n.t("schedulesForm.end")}
                                    />
                                  )}
                                </Field>
                              </Grid>
                            </Grid>
                          )}
                        </div>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            ></FieldArray>
            <div className={classes.buttonContainer}>
              <ButtonWithSpinner
                loading={loading}
                type="button"
                color="primary"
                variant="contained"
                onClick={handleSubmit}
              >
                {labelSaveButton ?? i18n.t("whatsappModal.buttons.okEdit")}
              </ButtonWithSpinner>
            </div>
          </div>
        );
      }}
    </Formik>
  );
}

export default SchedulesForm;
